import { pricingPlansModalMessages } from './pricing-plans-modal.messages';

import { ButtonAction } from '@heidi/analytics/vocabulary';
import { Period } from '@heidi/commerce/contracts';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useCallback, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import type { BillingPeriod } from '../../../../trials-and-upgrades/plan-period-tabs/types';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';
import {
  trackPricingPlansModalCta as trackCta,
  type PricingPlansModalCtaTrackArgs,
} from './track-pricing-plans-modal-cta';
import type { UsePricingPlansModalStateReturn } from './usePricingPlansModalState';

const SEAT_CHECKOUT_ROUTE = '/settings/billing/upgrade';
/** Individual clinician seat confirmation between the pricing modal and Stripe. */
const CLINICIAN_SEAT_CHECKOUT_ROUTE = '/settings/billing/clinician-upgrade';

export function usePricingPlansModalCtas(state: UsePricingPlansModalStateReturn) {
  const {
    currentPlan,
    currentSeatType,
    legacyTier,
    handlePlanCheckout,
    handleReactivateSubscription,
    handleClose,
    isTrialingClinician,
    isClinicianStandardPlan,
    isPracticeStandardPlan,
    isPracticeMaxSeat,
    isPracticeRisingTideCheckoutOffered,
    isInReverseTrial,
    isFakeClinicianTrial,
    isCancelPendingSubscription,
  } = state;

  const { formatMessage } = useIntl();
  const router = getCommerceBillingUiPorts().useAppRouter();
  // Clinician seat confirmation rejects team members; fall back to modal Stripe.
  const { hasTeam } = useCombinedQueriesTeamGeneralInfo();

  const clinicianProPlanCtaLabel = formatMessage(
    isClinicianStandardPlan
      ? isTrialingClinician
        ? pricingPlansModalMessages.upgradeNow
        : pricingPlansModalMessages.keepClinician
      : pricingPlansModalMessages.getClinician
  );

  // The Practice column is one card with two seats; each seat is "current"
  // only for the viewer who pays for that exact seat.
  const clinicianProPlusPlanCtaLabel = formatMessage(
    isPracticeStandardPlan
      ? isTrialingClinician
        ? pricingPlansModalMessages.upgradeNow
        : pricingPlansModalMessages.keepPractice
      : pricingPlansModalMessages.getPractice
  );

  const practiceMaxPlanCtaLabel = formatMessage(
    isPracticeMaxSeat
      ? isTrialingClinician
        ? pricingPlansModalMessages.upgradeNow
        : pricingPlansModalMessages.keepPracticeMax
      : pricingPlansModalMessages.getPracticeMax
  );

  const talkToSalesLabel = formatMessage(pricingPlansModalMessages.talkToSalesCta);

  const trackPricingPlansModalCta = useCallback(
    (
      trackArgs: Omit<
        PricingPlansModalCtaTrackArgs,
        'currentPlan' | 'currentSeatType' | 'isInReverseTrial' | 'isFakeClinicianTrial'
      >
    ) =>
      trackCta({
        ...trackArgs,
        currentPlan: currentPlan ?? legacyTier,
        currentSeatType,
        isInReverseTrial,
        isFakeClinicianTrial,
      }),
    [currentPlan, currentSeatType, legacyTier, isInReverseTrial, isFakeClinicianTrial]
  );

  const onClinicianProPlanCtaClick = useCallback(
    (period: Period) => {
      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
      const isOnCurrentPlan = isClinicianStandardPlan && !isTrialingClinician;
      const isKeepCurrentPlanClose = isOnCurrentPlan && !isCancelPendingSubscription;
      trackPricingPlansModalCta(
        isOnCurrentPlan
          ? {
              action: ButtonAction.PricingPlansModalKeepCurrentPlan,
              label: clinicianProPlanCtaLabel,
            }
          : {
              action: ButtonAction.PricingPlansModalGetPlan,
              label: clinicianProPlanCtaLabel,
              targetTier: SubscriptionTier.CLINICIAN_PRO,
              targetSeatType: BillingSeatType.STANDARD,
              period,
            }
      );
      if (isCancelPendingSubscription && isOnCurrentPlan) {
        handleReactivateSubscription();
      } else if (isKeepCurrentPlanClose) {
        handleClose();
      } else {
        void handlePlanCheckout(period, SubscriptionTier.CLINICIAN_PRO);
      }
    },
    [
      clinicianProPlanCtaLabel,
      handleClose,
      handlePlanCheckout,
      handleReactivateSubscription,
      isCancelPendingSubscription,
      isClinicianStandardPlan,
      isTrialingClinician,
      trackPricingPlansModalCta,
    ]
  );

  const handlePracticeCtaClick = useCallback(
    (
      period: Period,
      seatType: BillingSeatType,
      isCurrentSeat: boolean,
      label: string
    ) => {
      if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
      const isOnCurrentPlan = isCurrentSeat && !isTrialingClinician;
      const isKeepCurrentPlanClose = isOnCurrentPlan && !isCancelPendingSubscription;
      trackPricingPlansModalCta(
        isOnCurrentPlan
          ? {
              action: ButtonAction.PricingPlansModalKeepCurrentPlan,
              label,
            }
          : {
              action: ButtonAction.PricingPlansModalGetPlan,
              label,
              targetTier: SubscriptionTier.CLINICIAN_PRO_PLUS,
              targetSeatType: seatType,
              period,
            }
      );
      if (isCancelPendingSubscription && isOnCurrentPlan) {
        handleReactivateSubscription();
      } else if (isKeepCurrentPlanClose) {
        handleClose();
      } else if (isPracticeRisingTideCheckoutOffered) {
        // Rising-tide plans quote Standard/Max via invoice-preview on /upgrade.
        handleClose();
        void router.push(SEAT_CHECKOUT_ROUTE);
      } else {
        void handlePlanCheckout(period, SubscriptionTier.CLINICIAN_PRO_PLUS, seatType);
      }
    },
    [
      handleClose,
      handlePlanCheckout,
      handleReactivateSubscription,
      isCancelPendingSubscription,
      isPracticeRisingTideCheckoutOffered,
      isTrialingClinician,
      router,
      trackPricingPlansModalCta,
    ]
  );

  const onClinicianProPlusPlanCtaClick = useCallback(
    (period: Period) =>
      handlePracticeCtaClick(
        period,
        BillingSeatType.STANDARD,
        isPracticeStandardPlan,
        clinicianProPlusPlanCtaLabel
      ),
    [clinicianProPlusPlanCtaLabel, handlePracticeCtaClick, isPracticeStandardPlan]
  );

  const onPracticeMaxPlanCtaClick = useCallback(
    (period: Period) =>
      handlePracticeCtaClick(
        period,
        BillingSeatType.MAX,
        isPracticeMaxSeat,
        practiceMaxPlanCtaLabel
      ),
    [handlePracticeCtaClick, isPracticeMaxSeat, practiceMaxPlanCtaLabel]
  );

  const onTalkToSalesClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      trackPricingPlansModalCta({
        action: ButtonAction.PricingPlansModalGetInTouch,
        label: talkToSalesLabel,
        targetTier: SubscriptionTier.ENTERPRISE,
      });
      // The desktop webview ignores target="_blank"; hand the URL to the shell.
      if (getCommerceBillingUiPorts().isTauri()) {
        event.preventDefault();
        void getCommerceBillingUiPorts().openUrlInTauri(
          getCommerceBillingUiPorts().billingUrls.GET_IN_TOUCH_URL
        );
      }
    },
    [talkToSalesLabel, trackPricingPlansModalCta]
  );

  // Dual-seat Clinician always starts seat checkout — never a keep-current CTA.
  const trialEndClinicianProPlanCtaLabel = formatMessage(
    pricingPlansModalMessages.getClinician
  );

  /**
   * Trial-end Clinician CTA: seat confirmation for individuals; team viewers
   * keep modal Stripe checkout (host redirects hasTeam away).
   */
  const onTrialEndClinicianCtaClick = useCallback(() => {
    if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
    trackPricingPlansModalCta({
      action: ButtonAction.PricingPlansModalGetPlan,
      label: trialEndClinicianProPlanCtaLabel,
      targetTier: SubscriptionTier.CLINICIAN_PRO,
      targetSeatType: BillingSeatType.STANDARD,
      period: Period.ONE_YEAR,
    });
    if (hasTeam) {
      void handlePlanCheckout(Period.ONE_YEAR, SubscriptionTier.CLINICIAN_PRO);
      return;
    }
    handleClose();
    void router.push(CLINICIAN_SEAT_CHECKOUT_ROUTE);
  }, [
    trialEndClinicianProPlanCtaLabel,
    handleClose,
    handlePlanCheckout,
    hasTeam,
    router,
    trackPricingPlansModalCta,
  ]);

  /**
   * Trial-end Practice CTA: rising-tide plans → seat confirmation on /upgrade;
   * otherwise modal Stripe (team upgrades off, or already on Practice).
   */
  const onTrialEndPracticeCtaClick = useCallback(() => {
    if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return;
    trackPricingPlansModalCta({
      action: ButtonAction.PricingPlansModalGetPlan,
      label: clinicianProPlusPlanCtaLabel,
      targetTier: SubscriptionTier.CLINICIAN_PRO_PLUS,
      targetSeatType: BillingSeatType.STANDARD,
      period: Period.ONE_YEAR,
    });
    if (isPracticeRisingTideCheckoutOffered) {
      handleClose();
      void router.push(SEAT_CHECKOUT_ROUTE);
      return;
    }
    void handlePlanCheckout(Period.ONE_YEAR, SubscriptionTier.CLINICIAN_PRO_PLUS);
  }, [
    clinicianProPlusPlanCtaLabel,
    handleClose,
    handlePlanCheckout,
    isPracticeRisingTideCheckoutOffered,
    router,
    trackPricingPlansModalCta,
  ]);

  /** Monthly / yearly switch on the trial-end Clinician card. */
  const onTrialEndPeriodChange = useCallback(
    (period: BillingPeriod) => {
      trackPricingPlansModalCta({
        action: ButtonAction.PricingPlansModalPeriodSelected,
        label: period === 'annual' ? 'yearly' : 'monthly',
        targetTier: SubscriptionTier.CLINICIAN_PRO,
        period: period === 'annual' ? Period.ONE_YEAR : Period.ONE_MONTH,
      });
    },
    [trackPricingPlansModalCta]
  );

  return {
    clinicianProPlanCtaLabel,
    trialEndClinicianProPlanCtaLabel,
    clinicianProPlusPlanCtaLabel,
    practiceMaxPlanCtaLabel,
    onClinicianProPlanCtaClick,
    onClinicianProPlusPlanCtaClick,
    onPracticeMaxPlanCtaClick,
    onTrialEndClinicianCtaClick,
    onTrialEndPracticeCtaClick,
    onTrialEndPeriodChange,
    onTalkToSalesClick,
    isAppleCompliant: getCommerceBillingUiPorts().isAppleCompliantBuild(),
  };
}
